import React, { useCallback, useRef, useState } from 'react';
import { styled } from 'baseui';
import { useAtomValue } from 'jotai';
import { annotationMetadataAtom } from '../../../../../labelling-v2/atoms/annotation-atoms';
import useAnalyticsForVideoLabelling from '../../analytics/useAnalyticsForVideoLabelling';
import { getBasePayloadFromAnnotation } from '../analytics-utils';
import { useAnnotationObjects } from './hooks/use-annotation-objects';
import { useGetGridScrollGutterSize } from './hooks/use-get-grid-scroll-gutter-size';
import { useHandleScrollMouseEvents } from './hooks/use-handle-scroll-mouse-events';
import { AnnotationBodyGrid } from './annotation-body-grid';
import { ColumnHeaders } from './column-headers';
import { ANNOTATION_BODY_CONTRACTED_MAX_FRAMES, ANNOTATION_BODY_EXPANDED_MAX_FRAMES, FRAME_HEIGHT, getColumnHeaderHeight, ROW_HEADER_WIDTH } from './constants';
import { CurrentFrameMarker } from './current-frame-marker';
import type { AnnotationConfigModalPropsT } from './edit-annotation-panel';
import { EditAnnotationPanel } from './edit-annotation-panel';
import { HoveredColumn } from './hovered-column';
import { ObjectsLabel } from './objects-label';
import { RowHeaders } from './row-headers';
import { ScrollSelectedAnnotationInView } from './scroll-selected-annotation-in-view';
import type { CurrentFrameMarkerRefT, EditAnnotationPanelRefT, HoveredColumnRefT, OnAnnotationCellClick, ScrollGutterSizeT } from './types';
type PropsT = AnnotationConfigModalPropsT & {
  totalFrames: number;
  onHoveredFrameChange: (frameIndex: number | null) => void;
  onFrameClick: (frameIndex: number) => void;
  isExpanded: boolean;
  isDisabled: boolean;
};
const Container = styled('div', ({
  $theme
}) => ({
  position: 'relative',
  zIndex: 0,
  width: '100%',
  paddingTop: getColumnHeaderHeight($theme),
  paddingLeft: `${ROW_HEADER_WIDTH}px`,
  overflow: 'hidden'
}));
const BodyContainer = styled<'div', {
  $hasAnnotations: boolean;
  $numAnnotationsForHeight: number;
  $scrollGutterSize: ScrollGutterSizeT;
}>('div', ({
  $hasAnnotations,
  $numAnnotationsForHeight,
  $scrollGutterSize
}) => {
  const annotationCountForHeight = $hasAnnotations ? $numAnnotationsForHeight : 1;
  // Add half a frame height to provide:
  // - Space for horizontal scrollbar rendering (prevents scrollbar from
  //   overlapping content)
  // - Visual cue that more annotations exist beyond the visible area (e.g.,
  //   when 10 annotations are shown but 15 exist, the extra space hints at
  //   scrollable content)
  const extraHeightForScrollableIndication = 0.5 * FRAME_HEIGHT;
  const contentHeight = annotationCountForHeight * FRAME_HEIGHT + extraHeightForScrollableIndication;
  const totalHeight = contentHeight + $scrollGutterSize.bottom;
  return {
    height: `${totalHeight}px`,
    width: '100%',
    position: 'relative',
    cursor: 'pointer'
  };
});
export function AnnotationTimelineV2({
  totalFrames,
  onHoveredFrameChange,
  onFrameClick,
  showNotification,
  fetchAnnotationTrackingData,
  getBoundingBoxTrackingData,
  isExpanded,
  isDisabled
}: PropsT) {
  const annotationMetadata = useAtomValue(annotationMetadataAtom);

  // Build annotation IDs from metadata, maintaining stable insertion order.
  const {
    annotationObjectIds
  } = useAnnotationObjects(annotationMetadata);
  const columnStripRef = useRef<HTMLDivElement>(null);
  const rowStripRef = useRef<HTMLDivElement>(null);
  const hoveredColumnRef = useRef<HoveredColumnRefT>(null);
  const currentFrameMarkerRef = useRef<CurrentFrameMarkerRefT>(null);
  const editAnnotationPanelRef = useRef<EditAnnotationPanelRefT>(null);
  const [gridContainer, setGridContainer] = useState<HTMLDivElement | null>(null);
  const scrollGutterSize = useGetGridScrollGutterSize({
    gridContainer
  });
  const onFrameClickRef = useRef(onFrameClick);
  const {
    sendEventAnnotationTimelineSelection
  } = useAnalyticsForVideoLabelling();
  const {
    handleGridScroll,
    handleBodyMouseMove,
    handleBodyMouseLeave,
    getColumnIndexFromBodyClickEvent
  } = useHandleScrollMouseEvents({
    columnStripRef,
    rowStripRef,
    hoveredColumnRef,
    currentFrameMarkerRef,
    editAnnotationPanelRef,
    totalFrames
  });
  const handleCellClick = useCallback<OnAnnotationCellClick>((event, rowIndex, columnIndex, annotationData) => {
    if (isDisabled) {
      return;
    }
    onFrameClickRef.current(columnIndex);
    if (annotationData) {
      editAnnotationPanelRef.current?.open(event.currentTarget, annotationData);
      sendEventAnnotationTimelineSelection(getBasePayloadFromAnnotation(annotationData.annotationId, annotationData.annotationInfo, columnIndex + 1 // +1 to match the UI index
      ));
    }
  }, [onFrameClickRef, sendEventAnnotationTimelineSelection, isDisabled]);
  const handleBodyClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (isDisabled) {
      return;
    }
    const columnIndex = getColumnIndexFromBodyClickEvent(event);
    onFrameClickRef.current(columnIndex);
  }, [getColumnIndexFromBodyClickEvent, onFrameClickRef, isDisabled]);
  const hasAnnotations = annotationObjectIds.length > 0;
  const maxAnnotationFramesForHeight = isExpanded ? ANNOTATION_BODY_EXPANDED_MAX_FRAMES : ANNOTATION_BODY_CONTRACTED_MAX_FRAMES;
  const numAnnotationsForHeight = Math.min(annotationObjectIds.length, maxAnnotationFramesForHeight);
  return <Container>
      <ColumnHeaders totalFrames={totalFrames} containerStripRef={columnStripRef} scrollGutterSize={scrollGutterSize} />

      <RowHeaders annotationObjectIds={annotationObjectIds} containerStripRef={rowStripRef} scrollGutterSize={scrollGutterSize} />

      <ObjectsLabel hasAnnotations={hasAnnotations} />

      <BodyContainer $hasAnnotations={hasAnnotations} $numAnnotationsForHeight={numAnnotationsForHeight} $scrollGutterSize={scrollGutterSize} onMouseMove={handleBodyMouseMove} onMouseLeave={handleBodyMouseLeave} onClick={handleBodyClick}>
        <EditAnnotationPanel ref={editAnnotationPanelRef} showNotification={showNotification} fetchAnnotationTrackingData={fetchAnnotationTrackingData} getBoundingBoxTrackingData={getBoundingBoxTrackingData} />

        <HoveredColumn ref={hoveredColumnRef} onFrameChange={onHoveredFrameChange} isDisabled={isDisabled} totalFrames={totalFrames} />

        <CurrentFrameMarker ref={currentFrameMarkerRef} onFrameChange={onFrameClick} maxFrames={totalFrames} gridContainer={gridContainer} isDisabled={isDisabled} />

        <AnnotationBodyGrid numCols={totalFrames} annotationObjectIds={annotationObjectIds} onScroll={handleGridScroll} onCellClick={handleCellClick} gridContainer={gridContainer} setGridContainer={setGridContainer} />

        <ScrollSelectedAnnotationInView gridContainer={gridContainer} annotationObjectIds={annotationObjectIds} />
      </BodyContainer>
    </Container>;
}